import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { GuenstigerWirkstoff, WarenkorbPosition } from './warenkorb';
import { WarenkorbService } from './warenkorb.service';
import { UntypedFormControl } from '@angular/forms';
import {
    laborBestellEinheit,
    laborEinheitForm,
    laborInhaltAnzeige,
    laborMengePasstZumPack,
    laborPackFehler,
    laborPackInfo,
    Material,
} from '../../warehouse/material/material';
import { Supplier } from '../../warehouse/supplier/supplier';
import { SupplierMaterial } from '../../warehouse/supplier-material/supplier-material';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    ArticleScanAutocompleteComponent,
    ArticleScanSelectedEvent,
} from '../../components/article-scan-autocomplete/article-scan-autocomplete.component';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
    BestellPdfVorschauBlatt,
    BestellPdfVorschauDialogComponent,
    BestellPdfVorschauZeile,
} from '../../laboratory/behandlungs-artikel/add-behandlungsartikel-dialog/bestell-pdf-vorschau-dialog.component';

@Component({
    selector: 'kiene-warenkorb',
    templateUrl: './warenkorb.component.html',
    styleUrls: ['./warenkorb.component.scss'],
})
export class WarenkorbComponent implements OnInit {
    isLoading = false;
    warenkorbPositions: WarenkorbPosition[] = [];
    formCtrl: UntypedFormControl[] = [];
    positionKommentarCtrl: UntypedFormControl[] = [];
    inputDirtyArray: boolean[] = [];

    materialCtrl: UntypedFormControl = new UntypedFormControl();
    selectedMaterial: Material = new Material();

    positionAmountCtrl: UntypedFormControl = new UntypedFormControl();

    supplier: Supplier[] = [];

    @ViewChild('amountInput')
    amountInput: ElementRef;

    @ViewChild('articleAutocomplete')
    articleAutocomplete: ArticleScanAutocompleteComponent;

    kommentarCtrl = new UntypedFormControl();

    guenstiger: GuenstigerWirkstoff | null = null;
    private guenstigSeq = 0;

    /** true auf /warenkorb-labor bzw. bei reiner Laborleitung → nur Labor-/Diagnostik-Vorschläge */
    laborartikelOnly = false;

    constructor(
        private readonly service: WarenkorbService,
        public dialog: MatDialog,
        private readonly messageService: MessageService,
        private readonly localStorageService: LocalStorageService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
    ) {
        this.laborartikelOnly = this.computeLaborartikelOnly();
    }

    ngOnInit() {
        this.laborartikelOnly = this.computeLaborartikelOnly();
        this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
            this.laborartikelOnly = this.computeLaborartikelOnly();
        });
        this.loadWarenkorb();
    }

    private computeLaborartikelOnly(): boolean {
        // Route ist maßgeblich (nicht nur Rechte) – sonst sieht Admin/Praxis alle Artikel
        if (this.router.url.includes('warenkorb-labor')) {
            return true;
        }
        if (this.route.snapshot.data['laborWarenkorb'] || this.route.snapshot.routeConfig?.path === 'warenkorb-labor') {
            return true;
        }
        return (
            this.localStorageService.hasExplicitPermission(1082) &&
            !this.localStorageService.hasExplicitPermission(1081)
        );
    }

    loadWarenkorb() {
        this.isLoading = true;
        this.service.getWarenkorbContent(this.laborartikelOnly).subscribe(
            response => {
                this.warenkorbPositions = response.records;
                this.initFormControls();
                this.isLoading = false;
            },
            () => {
                this.isLoading = false;
            },
        );
    }

    initFormControls() {
        for (const w of this.warenkorbPositions) {
            this.inputDirtyArray[w.warenkorb_id] = false;
            const fc: UntypedFormControl = new UntypedFormControl();
            fc.setValue(w.menge_soll);
            fc.valueChanges.subscribe(() => {
                this.inputDirtyArray[w.warenkorb_id] = true;
            });
            let kommentarCtrl = new UntypedFormControl(w.kommentar);
            kommentarCtrl.valueChanges.subscribe(() => {
                this.inputDirtyArray[w.warenkorb_id] = true;
            });
            this.positionKommentarCtrl[w.warenkorb_id] = kommentarCtrl;
            this.formCtrl[w.warenkorb_id] = fc;
        }
    }

    materialSelected(asse: ArticleScanSelectedEvent) {
        this.selectedMaterial = asse.article;
        this.amountInput.nativeElement?.focus();
        this.ladeGuenstiger();
    }

    materialCleared() {
        this.selectedMaterial = new Material();
        this.guenstigSeq++;
        this.guenstiger = null;
    }

    uebernehmenGuenstiger() {
        const alt = this.guenstiger?.alternativ;
        if (!alt?.artikel_id) {
            return;
        }
        const material = new Material();
        material.artikel_id = alt.artikel_id;
        material.bezeichnung = alt.bezeichnung;
        material.artikel_me = alt.artikel_me;
        material.lieferant = alt.lieferant;
        material.menge = alt.menge;
        material.einheit = alt.einheit;
        material.warengruppe = alt.warengruppe;
        this.articleAutocomplete.setMaterial(material, false);
        this.selectedMaterial = material;
        this.ladeGuenstiger();
    }

    guenstigerText(): string {
        const alt = this.guenstiger?.alternativ;
        if (!alt) {
            return '';
        }
        const basis = alt.einheit_basis || this.guenstiger.einheit_basis;
        const lieferant = String(alt.lieferant || '').trim();
        const name = alt.artikel_me || alt.bezeichnung;
        return (
            'Günstiger mit dem Wirkstoff ' +
            this.guenstiger.wirkstoff +
            ': ' +
            name +
            (lieferant ? ', ' + lieferant : '') +
            ', ' +
            this.euro(alt.ek_preis) +
            ' (' +
            this.euroJeEinheit(alt.preis_einheit, basis) +
            ' statt ' +
            this.euroJeEinheit(this.guenstiger.eigen_preis_einheit, basis) +
            ').'
        );
    }

    private ladeGuenstiger() {
        this.guenstiger = null;
        if (this.laborartikelOnly || !(this.selectedMaterial?.artikel_id > 0)) {
            this.guenstigSeq++;
            return;
        }
        const seq = ++this.guenstigSeq;
        const artikelId = this.selectedMaterial.artikel_id;
        this.service.guenstigerWirkstoff(artikelId).subscribe({
            next: res => {
                if (seq !== this.guenstigSeq || this.selectedMaterial?.artikel_id !== artikelId) {
                    return;
                }
                this.guenstiger = res?.alternativ ? res : null;
            },
            error: () => {
                if (seq === this.guenstigSeq) {
                    this.guenstiger = null;
                }
            },
        });
    }

    private euro(wert: number): string {
        return (
            Number(wert).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
        );
    }

    private euroJeEinheit(wert: number, basis: string): string {
        const digits = Number(wert) >= 1 ? 2 : 4;
        const text = Number(wert).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: digits,
        });
        return basis ? text + ' €/' + basis : text + ' € je Einheit';
    }

    /** Nur wenn eine Packung wirklich einen Inhalt größer als 1 hat. */
    get gebindeHinweis(): string {
        if (!this.selectedMaterial?.artikel_id) {
            return '';
        }
        const inhalt =
            String(this.selectedMaterial.einheit_roh || '').trim() ||
            laborInhaltAnzeige(this.selectedMaterial.einheit, this.selectedMaterial.menge);
        if (!inhalt) {
            return '';
        }
        return `1 ${this.packNameMaterial()} enthält ${inhalt}`;
    }

    packNameMaterial(anzahl: unknown = 1): string {
        if (!this.selectedMaterial?.artikel_id) {
            return laborEinheitForm('Stück', anzahl);
        }
        return laborEinheitForm(
            laborBestellEinheit(
                this.selectedMaterial.einheit_alternativ,
                this.selectedMaterial.einheit,
                this.selectedMaterial.menge,
            ),
            anzahl,
        );
    }

    packNamePosition(w: WarenkorbPosition): string {
        const menge = this.formCtrl[w?.warenkorb_id] ? this.formCtrl[w.warenkorb_id].value : w?.menge_soll;
        return laborEinheitForm(laborBestellEinheit(w?.einheit_alternativ, w?.einheit), menge);
    }

    inhaltHinweis(w: WarenkorbPosition): string {
        const roh = String(w?.einheit_roh || '').trim();
        return roh || laborInhaltAnzeige(w?.einheit);
    }

    artikelTitel(w: WarenkorbPosition): string {
        const name = String(w?.artikel || '').trim();
        const inhalt = this.inhaltHinweis(w);
        return inhalt ? name + ' (' + inhalt + ')' : name;
    }

    packInfoAdd(): string {
        return this.laborartikelOnly ? laborPackInfo(this.selectedMaterial?.gebindegroesse) : '';
    }

    packFehlerAdd(): string {
        return this.laborartikelOnly
            ? laborPackFehler(this.positionAmountCtrl.value, this.selectedMaterial?.gebindegroesse)
            : '';
    }

    packInfoPosition(w: WarenkorbPosition): string {
        return this.laborartikelOnly ? laborPackInfo(w?.gebindegroesse) : '';
    }

    packFehlerPosition(w: WarenkorbPosition): string {
        const menge = this.formCtrl[w.warenkorb_id] ? this.formCtrl[w.warenkorb_id].value : w.menge_soll;
        return this.laborartikelOnly ? laborPackFehler(menge, w?.gebindegroesse) : '';
    }

    mindestbestellTextAdd(): string {
        return this.mindestbestellText(
            this.selectedMaterial?.bestellmenge,
            this.selectedMaterial?.einheit_alternativ,
            this.selectedMaterial?.einheit,
            this.selectedMaterial?.menge,
        );
    }

    mindestbestellTextPosition(w: WarenkorbPosition): string {
        return this.mindestbestellText(w.bestellmenge, w.einheit_alternativ, w.einheit);
    }

    private mindestbestellText(bestellmenge: number, pack: string, inhalt?: string, menge?: number): string {
        if (!this.laborartikelOnly) {
            return '';
        }
        const min = Number(bestellmenge);
        if (!(min > 0)) {
            return '';
        }
        return `Mindestbestellmenge: ${min} ${laborEinheitForm(laborBestellEinheit(pack, inhalt, menge), min)}`;
    }

    openBestellvorschauAdd(): void {
        const material = this.selectedMaterial;
        if (!material?.artikel_id) {
            return;
        }
        const anzahl = Number(this.positionAmountCtrl.value);
        const zeile = this.pdfZeile(
            material.bezeichnung,
            material.einheit_roh || material.einheit,
            anzahl,
            material.einheit_alternativ,
            material.menge,
        );
        const lieferant = String(material.lieferant_labor || material.lieferant || '').trim();
        this.openBestellvorschau([{ lieferant, positionen: [zeile] }]);
    }

    openBestellvorschauWarenkorb(): void {
        const byLieferant = new Map<string, BestellPdfVorschauZeile[]>();
        for (const w of this.warenkorbPositions) {
            const lieferant = String(w.lieferant || '').trim();
            const menge = this.formCtrl[w.warenkorb_id] ? Number(this.formCtrl[w.warenkorb_id].value) : Number(w.menge_soll);
            const zeile = this.pdfZeile(w.artikel, w.einheit_roh || w.einheit, menge, w.einheit_alternativ, w.menge);
            const positionen = byLieferant.get(lieferant) || [];
            positionen.push(zeile);
            byLieferant.set(lieferant, positionen);
        }
        const blaetter: BestellPdfVorschauBlatt[] = [];
        byLieferant.forEach((positionen, lieferant) => blaetter.push({ lieferant, positionen }));
        this.openBestellvorschau(blaetter);
    }

    private openBestellvorschau(blaetter: BestellPdfVorschauBlatt[]): void {
        this.dialog.open(BestellPdfVorschauDialogComponent, {
            width: '760px',
            autoFocus: false,
            data: { blaetter },
        });
    }

    private pdfZeile(
        name: string,
        inhalt: string,
        anzahlRaw: number,
        pack: string,
        menge?: number,
    ): BestellPdfVorschauZeile {
        const bezeichnung = String(name || '').trim() || 'Bezeichnung';
        const anzeige = laborInhaltAnzeige(inhalt, menge);
        const anzahl = anzahlRaw > 0 ? anzahlRaw : 1;
        return {
            artikel: bezeichnung,
            inhalt: anzeige,
            menge: anzahl + ' ' + laborEinheitForm(laborBestellEinheit(pack, anzeige || inhalt, menge), anzahl),
        };
    }

    mindestHinweisAdd(): string {
        return this.mindestHinweis(
            this.positionAmountCtrl.value,
            this.selectedMaterial?.bestellmenge,
            this.selectedMaterial?.einheit_alternativ,
            this.selectedMaterial?.einheit,
            this.selectedMaterial?.menge,
        );
    }

    mindestHinweisPosition(w: WarenkorbPosition): string {
        const menge = this.formCtrl[w.warenkorb_id] ? this.formCtrl[w.warenkorb_id].value : w.menge_soll;
        return this.mindestHinweis(menge, w.bestellmenge, w.einheit_alternativ, w.einheit);
    }

    private mindestHinweis(
        menge: number,
        bestellmenge: number,
        pack: string,
        inhalt?: string,
        inhaltMenge?: number,
    ): string {
        if (!this.laborartikelOnly) {
            return '';
        }
        const min = Number(bestellmenge);
        const anzahl = Number(menge);
        if (!(min > 0) || !(anzahl > 0) || anzahl >= min) {
            return '';
        }
        const einheit = laborEinheitForm(laborBestellEinheit(pack, inhalt, inhaltMenge), min);
        return `Mindestbestellmenge ${min} ${einheit} ist noch nicht erreicht.`;
    }

    updateLieferant(w: WarenkorbPosition, sm: SupplierMaterial) {
        w.lieferant_id = sm.lieferant_id;
        this.service.updateWarenkorbPosition(w).subscribe(
            () => {
                this.loadWarenkorb();
            },
            error => {
                this.messageService.errorMessage('Fehler beim Aktualisieren des Lieferanten aufgetreten!', error);
            },
        );
    }

    addPosition() {
        const amount: number = this.positionAmountCtrl.value;
        if (this.laborartikelOnly) {
            const wg = String(this.selectedMaterial?.warengruppe || '');
            if (wg !== 'Diagnostikartikel') {
                this.messageService.errorMessage(
                    'Im Labor-Bestellungswarenkorb sind nur Diagnostikartikel erlaubt.',
                    null,
                );
                return;
            }
            if (!laborMengePasstZumPack(amount, this.selectedMaterial?.gebindegroesse)) {
                this.messageService.errorMessage(this.packFehlerAdd() || 'Menge passt nicht zur Packung.', null);
                return;
            }
        }

        this.service
            .addArticleToWarenkorb(
                this.selectedMaterial.artikel_id,
                amount,
                this.kommentarCtrl.value,
                this.laborartikelOnly,
            )
            .subscribe(
                response => {
                    const mengeText = this.laborartikelOnly
                        ? response.menge_soll + ' ' + this.packNameMaterial(response.menge_soll) + ' '
                        : response.menge_soll + 'x ';
                    this.messageService.infoMessage(
                        mengeText + this.selectedMaterial.bezeichnung + ' dem Warenkorb hinzugefügt!',
                    );
                    this.positionAmountCtrl.setValue(null);
                    this.kommentarCtrl.setValue(null);
                    this.guenstigSeq++;
                    this.guenstiger = null;
                    this.loadWarenkorb();
                    this.service.updateShoppingCartBadge();
                    this.articleAutocomplete.clearAndFocus();
                },
                error => {
                    this.messageService.errorMessage(
                        'Konnte ' +
                            amount +
                            'x ' +
                            this.selectedMaterial.bezeichnung +
                            ' nicht dem Warenkorb hinzufügen!',
                        error,
                    );
                },
            );
    }

    updateWarenkorbPosition(w: WarenkorbPosition) {
        const menge: number = this.formCtrl[w.warenkorb_id].value;
        if (this.laborartikelOnly && !laborMengePasstZumPack(menge, w.gebindegroesse)) {
            this.messageService.errorMessage(laborPackFehler(menge, w.gebindegroesse) || 'Menge passt nicht zur Packung.', null);
            return;
        }
        w.menge_soll = menge;
        w.kommentar = this.positionKommentarCtrl[w.warenkorb_id].value;
        this.service.updateWarenkorbPosition(w).subscribe(
            () => {
                this.loadWarenkorb();
            },
            () => {},
        );
    }

    removeWarenkorbPosition(w: WarenkorbPosition) {
        this.service.deleteWarenkorbPosition(w).subscribe(
            () => {
                this.loadWarenkorb();
                this.service.updateShoppingCartBadge();
            },
            () => {},
        );
    }

    releaseWarenkorb() {
        this.service.releaseWarenkorb(this.laborartikelOnly).subscribe(() => {
            this.service.updateShoppingCartBadge();
            this.loadWarenkorb();
        });
    }

    openReleaseWarenkorbConfirmDialog() {
        const dialogRef = this.dialog.open(ReleaseWarenkorbConfirmDialog, {
            width: '500px',
            data: {
                title: 'Bestellung freigeben',
                text: 'Wollen Sie die Bestellung freigeben?',
                okButtonText: 'Freigeben',
            },
        });

        dialogRef.afterClosed().subscribe(response => {
            if (response) {
                this.releaseWarenkorb();
            }
        });
    }
}

@Component({
    selector: 'confirm-release-warenkorb-dialog',
    template: `<h3 mat-dialog-title>{{ title }}</h3>
        <mat-dialog-content>
            <p>{{ text }}</p>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button mat-button color="primary" (click)="onCancelClick()">Abbrechen</button>
            <button mat-raised-button color="primary" (click)="confirmDelete()">
                {{ okButtonText }}
            </button>
        </mat-dialog-actions>`,
})
export class ReleaseWarenkorbConfirmDialog {
    title: string;
    text: string;
    okButtonText: string;

    constructor(
        public dialogRef: MatDialogRef<ReleaseWarenkorbConfirmDialog>,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
        this.text = data.text;
        this.title = data.title;
        this.okButtonText = data.okButtonText;
    }

    confirmDelete() {
        this.dialogRef.close(true);
    }

    onCancelClick(): void {
        this.dialogRef.close(false);
    }
}
